'use strict';

/**
 * See README.md for full description
 *
 * @module redux-middleware-adapter
 */

const stage = {
    before: 'before',
    after: 'after'
};

let registeredMiddleware = {
    [stage.before]: {},
    [stage.after]: {}
};

/**
 * middlewareAdapter is applied to the redux store. It is a standard redux middleware.
 * It is invoked after each dispatched action, prior to any reducer being called.
 * middlewareAdapter maintains a map of action types and callbacks. In response
 * to an action, it will invoke each of the callback functions that are
 * associated with that action's type.
 */
const middlewareAdapter = (store) => (next) => (action) => {
    if (registeredMiddleware[stage.before][action.type]) {
        registeredMiddleware[stage.before][action.type].forEach((middleware) => middleware(store, action));
    }

    let result = next(action);

    if (registeredMiddleware[stage.after][action.type]) {
        registeredMiddleware[stage.after][action.type].reduce((_result, middleware) => middleware(store, action), result);
    }

    // return the state to follow Redux convention
    return result;
};

/**
 * registerMiddleware takes an actionType and a callback function.
 * When an action of the provided type is dispatched, the callback function will
 * be invoked by the middlewareAdapter.
 * The provided callback function takes 3 parameters, the store and the action.
 * These parameters may be omitted if they are not needed.
 * @param {String} actionType action type string of the action that is dispatched that the function is concerned with
 * @param {func} func middleware function that is listening to the action provided
 * @param {String} callTiming This can be either "before" or "after" and tells middleware adapter to call the middleware function
 * before or after the redux state is updated by the reducers handling the same action type. Default: "before"
 */
const registerMiddleware = (actionType, func, callTiming = stage.before) => {
    /* istanbul ignore else */
    if (!registeredMiddleware[callTiming][actionType]) {
        registeredMiddleware[callTiming][actionType] = [];
    }

    // Only push the function on if it doesn't already exist. This protects against having multiple
    // copies of the same function which would happen during server-side initialization.
    if (registeredMiddleware[callTiming][actionType].indexOf(func) === -1) {
        registeredMiddleware[callTiming][actionType].push(func);
    }
};

module.exports = {registerMiddleware, middlewareAdapter, registeredMiddleware};
